import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import MonacoEditor, { Monaco, loader } from "@monaco-editor/react";
import { ExpandMore } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  styled,
  Tooltip,
  Typography as T,
} from "@mui/material";
import useDatasetTable from "datasets/hooks/useDatasetTable";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import useConfiguratorContext from "datasets/pages/settings/contexts/configurator";
import { dissocPath } from "ramda";
import Loading from "~/components/Loading";
import useErrorToast from "~/contexts/error-toast";
import { useFetcher } from "~/hooks/useFetcher";
import * as monaco from "~/utils/monaco-editor";
import rumClient from "~/utils/monitoring";

loader.config({ monaco });

const SCHEMA_URI = "/api/cohort-explorer/configurator/metadata/columns-schema.json";

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  "&.MuiButton-outlinedPrimary": {
    color: "#707684",
    borderColor: "#E0E0E7",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      borderColor: theme.palette.primary.dark,
    },
    "& .MuiLoadingButton-loadingIndicator": {
      color: theme.palette.primary.dark,
    },
  },
}));

const useIsPrimaryDomain = () => {
  const { domainId } = useParams();
  const { config, loading } = useConfiguratorContext();
  const configDomains = config?.content?.tables ?? [];

  if (loading) {
    // Not sure until config loads
    return null;
  }

  // First existing config domain matches current domain or no domains exist for config and this is the first one created
  return configDomains.length ? domainId === configDomains[0].id : domainId === "new";
};

const useJSONSchema = () => {
  const isPrimary = useIsPrimaryDomain();
  const resp = useFetcher<any>(isPrimary !== null ? SCHEMA_URI : null, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  return useMemo(() => {
    if (resp.data) {
      let schema = resp.data;
      if (!isPrimary) {
        // Remove breakdown field from JSON schema if the domain is not the primary (first in list)
        schema = dissocPath(["$defs", "BreakdownConfig"], schema);
        schema = dissocPath(["$defs", "ColumnConfig", "properties", "breakdown"], schema);
      }
      return schema;
    }
  }, [resp.data, isPrimary]);
};

const JSONEditor = () => {
  const jsonSchema = useJSONSchema();
  const { showError } = useErrorToast();
  const monacoRef = useRef<Monaco | null>(null);
  const { formState, resetField, watch, trigger } = useFormContext();
  const selectedTable = watch("table");
  const { data: tableFQNs } = useDatasetTableFQNs();
  const tableFQN = tableFQNs?.find((fqn) => fqn.endsWith(selectedTable));
  const { data: tableInfo, isLoading: loadingColumns } = useDatasetTable(tableFQN);
  const [columnAnchorEl, setColumnAnchorEl] = useState<null | HTMLElement>(null);
  const [isFetchingJson, setIsFetchingJson] = useState(false);
  const tableColumns = tableInfo?.columns;

  const isPrimaryDomain = useIsPrimaryDomain();

  const { field: columnsField } = useController({
    name: "columns",
    rules: {
      required: true,
      validate: (value) => {
        try {
          JSON.parse(value);

          if (monacoRef.current) {
            // Check against JSON schema
            const markers = monacoRef.current.editor?.getModelMarkers({ owner: "json" });
            const hasErrors = markers.some(
              (marker) => marker.severity === monaco.MarkerSeverity.Error
            );

            if (hasErrors) {
              return "Invalid JSON";
            }
          }
        } catch (e) {
          return "Invalid JSON";
        }

        return true;
      },
    },
  });

  const formattedValue = useMemo(() => {
    try {
      const parsed = JSON.parse(columnsField.value);
      const formatted = parsed.map((column: any) => {
        if (!isPrimaryDomain) {
          // If a domain is not primary, remove `breakdown` property from editor
          return dissocPath(["breakdown"], column);
        }
        return column;
      });
      return JSON.stringify(formatted, null, 2);
    } catch (e) {
      return columnsField.value;
    }
  }, [columnsField.value, isPrimaryDomain]);

  useEffect(() => {
    if (monacoRef.current && jsonSchema) {
      monacoRef.current.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [
          {
            uri: SCHEMA_URI,
            fileMatch: ["*"],
            schema: jsonSchema,
          },
        ],
        schemaValidation: "error",
      });

      // Force re-validation once markers are updated (async)
      monacoRef.current.editor?.onDidChangeMarkers(() => trigger("columns"));
    }
  }, [monacoRef.current, jsonSchema]);

  useEffect(() => {
    if (monacoRef.current && selectedTable) {
      resetField("columns");
      columnsField.onChange(JSON.stringify([]));
    }
  }, [selectedTable]);

  const handleColumnMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setColumnAnchorEl(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnAnchorEl(null);
  };

  const handleInsertColumnJson = async (newColumn: string) => {
    setIsFetchingJson(true);
    handleColumnMenuClose();

    try {
      const domain = selectedTable.split(".");
      const response = await fetch(
        `/api/cohort-explorer/configurator/metadata/${domain[0]}/${domain[1]}/${newColumn}/raw`
      );

      if (!response.ok) {
        throw Error("There was a problem trying to insert column JSON");
      }

      const newColumnResp = await response.json();

      if (!isPrimaryDomain) {
        // Remove breakdown property if domain is not primary
        delete newColumnResp.breakdown;
      }

      const currentJson = JSON.parse(columnsField.value);

      currentJson.push(newColumnResp);

      columnsField.onChange(JSON.stringify(currentJson, null, 2));
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to insert column JSON");
    }

    setIsFetchingJson(false);
  };

  if (!selectedTable) {
    return null;
  }

  return (
    <Stack gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <T variant="h6" fontWeight={550} fontSize={18}>
          Configuration (JSON)
        </T>
        <Tooltip
          title="Insert a column from the source table"
          arrow
          placement="top"
          enterDelay={750}
        >
          <span>
            <StyledLoadingButton
              data-testid="column-template-insert"
              disabled={loadingColumns || formState.isSubmitting}
              endIcon={<ExpandMore />}
              loading={loadingColumns || isFetchingJson}
              loadingPosition="end"
              onClick={handleColumnMenuOpen}
              sx={{ justifyContent: "space-between", width: 260 }}
              variant="outlined"
            >
              Insert column into JSON...
            </StyledLoadingButton>
          </span>
        </Tooltip>
        <Popover
          anchorEl={columnAnchorEl}
          open={!!columnAnchorEl}
          onClose={handleColumnMenuClose}
          PaperProps={{
            style: {
              maxHeight: 400,
              width: 260,
            },
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <List dense disablePadding>
            {tableColumns?.map((c, i) => (
              <ListItem key={i} disableGutters disablePadding>
                <ListItemButton
                  disabled={isFetchingJson}
                  onClick={() => {
                    handleInsertColumnJson(c.name);
                  }}
                >
                  <ListItemText>{c.name}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Popover>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          flexGrow: 1,
          border: "1px solid #E0E0E7",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <MonacoEditor
          height="500px"
          language="json"
          theme="vs-dark"
          loading={<Loading />}
          value={formattedValue}
          beforeMount={(monaco) => {
            monacoRef.current = monaco;
          }}
          onChange={(value) => columnsField.onChange(value || "")}
          // Force re-validation once markers are updated (async)
          onValidate={() => trigger("columns")}
          options={{
            automaticLayout: true,
            formatOnType: true,
            formatOnPaste: true,
            minimap: { enabled: false },
            readOnly: isFetchingJson || formState.isSubmitting,
            scrollBeyondLastLine: false,
            fixedOverflowWidgets: true, // Allow validation tooltips to escape container
          }}
        />
      </Box>
    </Stack>
  );
};

export default JSONEditor;
